import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

import { httpClient } from "@/api/client";
import type { SDocumentType } from "types";

export function getQueryKeyByGroupId(groupId?: string) {
  return ["comments", groupId];
}

export function useAddCommentMutation({
  type,
  groupId,
}: {
  type: SDocumentType;
  groupId: string;
}) {
  const queryClient = useQueryClient();

  let commentType:
    | "BEST_PRACTICE_TOPIC_DOCUMENT_COMMENT"
    | "SOFTWARE_DOCUMENT_COMMENT";

  switch (type) {
    case "SOFTWARE_CHECKLIST":
      commentType = "SOFTWARE_DOCUMENT_COMMENT";
      break;
    case "IT_BEST_PRACTICE":
      commentType = "BEST_PRACTICE_TOPIC_DOCUMENT_COMMENT";
      break;
  }

  return useMutation({
    mutationFn: (variables: { comment: string }) => {
      return axios.post(`/comment-groups/${groupId}/comments`, {
        type: commentType,
        comment: variables.comment.trim(),
      });
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getQueryKeyByGroupId(groupId),
      });
    },
  });
}

export function getCommentsUrl(groupId: string) {
  return `/comment-groups/${groupId}/comments`;
}

export function useComments({
  groupId,
  withHiddenComments = false,
}: { groupId: string; withHiddenComments?: boolean }) {
  return useQuery({
    queryKey: [...getQueryKeyByGroupId(groupId), withHiddenComments],
    queryFn: async () => {
      const { data } = await httpClient.paths[
        "/v1/comment-groups/{groupId}/comments"
      ].get({
        groupId,
        withHiddenComments,
      });
      return data;
    },
    enabled: !!groupId,
  });
}

export function useDeleteCommentMutation({
  commentId,
  groupId,
}: {
  commentId: string;
  groupId: string;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => {
      return axios.delete(`/comment-groups/${groupId}/comments/${commentId}`);
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getQueryKeyByGroupId(groupId),
      });
    },
  });
}

export function useEditCommentMutation({
  groupId,
  commentId,
}: {
  groupId: string;
  commentId: string;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: { comment: string }) => {
      return axios.patch(`/comment-groups/${groupId}/comments/${commentId}`, {
        comment: variables.comment.trim(),
      });
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getQueryKeyByGroupId(groupId),
      });
    },
  });
}
